import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Button, Card, Chip, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getNews } from '../../api/microcms/news';
import { black, blue } from '../../constants/Colors';
import { screenType } from '../../constants/enum';

import CustomLoading from './CustomLoading';

const styles = {
  body: {
    img: {
      height: 'auto',
      maxWidth: '100%'
    }
  },
  button: {
    color: blue
  },
  card: {
    '@media screen and (max-width:480px)': {
      margin: '16px',
      padding: '32px 16px'
    },
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    margin: '24px',
    padding: '40px'
  },
  date: {
    color: black
  },
  divider: {
    bgcolor: '#DBDBE0',
    border: 'none',
    flex: 1,
    height: '1px'
  },
  dividerDec: {
    bgcolor: black,
    height: '3px',
    width: '72px'
  },
  tag: {
    borderRadius: '4px',
    fontSize: '12px',
    height: '24px',
    ml: '12px'
  },
  title: {
    color: black,
    fontSize: '18px',
    fontWeight: 700
  }
};

interface Props {
  screen: number;
}

const MypageNewsDetail = (props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { screen } = props;
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [data, setData] = React.useState<{
    body: string;
    category: string;
    date: string;
    id: string;
    title: string;
  }>({
    body: '',
    category: '',
    date: '',
    id: id ?? '',
    title: ''
  });

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return `${year}/${month}/${day}`;
  };

  const decodeHtml = (html: string): string => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  useEffect(() => {
    void (async () => {
      if (id) {
        setLoading(true);
        const newsData = await getNews(id);
        setData({
          body: decodeHtml(newsData.content),
          category: newsData.category[0],
          date: formatDate(newsData.publishedAt),
          id: newsData.id,
          title: newsData.title
        });
        setLoading(false);
      }
    })();
  }, [id]);

  const chipColor = (category: string): object => {
    switch (category) {
      case t('mypage.news.category.1'):
        return {
          backgroundColor: '#EBF5FA',
          border: '1px solid #0A7EBF',
          color: '#0A7EBF'
        };
      case t('mypage.news.category.2'):
        return {
          backgroundColor: '#FBF3EB',
          border: '1px solid #D06604',
          color: '#D06604'
        };
      case t('mypage.news.category.3'):
        return {
          backgroundColor: '#ECF5F0',
          border: '1px solid #177D46',
          color: '#177D46'
        };
      case t('mypage.news.category.4'):
        return {
          backgroundColor: '#F3EBFA',
          border: '1px solid #6A00BD',
          color: '#6A00BD'
        };
      case t('mypage.news.category.5'):
        return {
          backgroundColor: '#FAECEC',
          border: '1px solid #BB1715',
          color: '#BB1715'
        };
      default:
        return {
          backgroundColor: '#F3F3F3',
          border: '1px solid #757575',
          color: '#757575'
        };
    }
  };

  const backNewsList = (): void => {
    switch (screen) {
      case screenType.client:
        navigate('/client/mypage/news');
        break;
      case screenType.agent:
        navigate('/agent/mypage/news');
        break;
      case screenType.publisher:
        navigate('/publisher/mypage/news');
        break;
      default:
        navigate('/client/mypage/news');
        break;
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid>
        <Card sx={styles.card}>
          <Grid container flexDirection={'column'} rowGap={'16px'}>
            <Grid container flexDirection={'row'}>
              <Typography sx={styles.date}>{data.date}</Typography>
              <Chip
                label={data.category}
                sx={[styles.tag, chipColor(data.category)]}
              />
            </Grid>
            <Typography sx={styles.title}>{data.title}</Typography>
            <Grid container flexDirection={'row'} alignItems={'flex-end'}>
              <Divider sx={styles.dividerDec} />
              <Divider sx={styles.divider} />
            </Grid>
          </Grid>
          <Grid
            sx={{
              margin: '24px 0 32px 0'
            }}
          >
            <Typography
              component="p"
              sx={styles.body}
              dangerouslySetInnerHTML={{ __html: data.body }}
            />
          </Grid>
          <Button sx={styles.button} onClick={backNewsList}>
            <ChevronLeftIcon sx={{ mr: '8px' }} />
            {t('mypage.news.backNews')}
          </Button>
        </Card>
      </Grid>
    </>
  );
};

export default MypageNewsDetail;
